// Supabase Auth adapter — implements AuthPort + TokenProvider.
//
// Anonymous-first: the app works without a login (3 trial credits are attributed
// to an anonymous Supabase user). Upgrading to a real account (Apple/Google/magic
// link) keeps the same user id, so credits carry over.
//
// Depends only on a narrow structural slice of the supabase-js auth client
// (`SupabaseAuthLike`), so it is unit-testable with a fake and never imports the
// SDK type surface directly. The real wiring lives in createSupabaseAuth().

import type { AuthPort, SessionUser, TokenProvider } from '../bridge/ports';

// ---- narrow structural view of `supabase.auth` we actually use --------------

interface SbUser {
  id: string;
  email?: string | null;
  is_anonymous?: boolean;
}

interface SbSession {
  access_token: string;
  user: SbUser;
}

interface SbResult<T> {
  data: T;
  error: { message: string } | null;
}

export interface SupabaseAuthLike {
  getUser(): Promise<SbResult<{ user: SbUser | null }>>;
  getSession(): Promise<SbResult<{ session: SbSession | null }>>;
  signInAnonymously(): Promise<SbResult<{ user: SbUser | null; session: SbSession | null }>>;
  signInWithOAuth(args: {
    provider: 'apple' | 'google';
    options?: { redirectTo?: string };
  }): Promise<SbResult<unknown>>;
  signInWithOtp(args: { email: string }): Promise<SbResult<unknown>>;
  signOut(): Promise<{ error: { message: string } | null }>;
  onAuthStateChange(cb: (event: string, session: SbSession | null) => void): {
    data: { subscription: { unsubscribe(): void } };
  };
}

function toSessionUser(u: SbUser): SessionUser {
  return {
    id: u.id,
    email: u.email ?? null,
    isAnonymous: u.is_anonymous === true,
  };
}

function unwrap<T>(res: SbResult<T>, ctx: string): T {
  if (res.error) throw new Error(`supabase_auth_${ctx}: ${res.error.message}`);
  return res.data;
}

export class SupabaseAuth implements AuthPort, TokenProvider {
  constructor(private readonly auth: SupabaseAuthLike) {}

  async getUser(): Promise<SessionUser | null> {
    const { user } = unwrap(await this.auth.getUser(), 'getUser');
    return user ? toSessionUser(user) : null;
  }

  async ensureSession(): Promise<SessionUser> {
    const existing = unwrap(await this.auth.getUser(), 'getUser');
    if (existing.user) return toSessionUser(existing.user);

    const created = unwrap(await this.auth.signInAnonymously(), 'signInAnonymously');
    if (!created.user) throw new Error('supabase_auth_anon: no user returned');
    return toSessionUser(created.user);
  }

  async signInWithProvider(provider: 'apple' | 'google'): Promise<SessionUser> {
    const redirectTo = typeof window !== 'undefined' ? window.location.origin : undefined;
    unwrap(
      await this.auth.signInWithOAuth({
        provider,
        ...(redirectTo ? { options: { redirectTo } } : {}),
      }),
      'signInWithOAuth',
    );
    // OAuth completes via redirect/deep link; the post-redirect session is read
    // by onAuthChange / getUser. Resolve with whatever user we currently have so
    // callers awaiting this don't hang (anonymous until the redirect returns).
    const after = await this.getUser();
    if (after) return after;
    return { id: '', email: null, isAnonymous: true };
  }

  async signInWithEmail(email: string): Promise<void> {
    unwrap(await this.auth.signInWithOtp({ email }), 'signInWithOtp');
  }

  async signOut(): Promise<void> {
    const { error } = await this.auth.signOut();
    if (error) throw new Error(`supabase_auth_signOut: ${error.message}`);
  }

  onAuthChange(cb: (user: SessionUser | null) => void): () => void {
    const { data } = this.auth.onAuthStateChange((_event, session) => {
      cb(session?.user ? toSessionUser(session.user) : null);
    });
    return () => data.subscription.unsubscribe();
  }

  // ---- TokenProvider ------------------------------------------------------
  async getAccessToken(): Promise<string | null> {
    const { session } = unwrap(await this.auth.getSession(), 'getSession');
    return session?.access_token ?? null;
  }
}
